import React, { useEffect, useRef, useState } from "react";
import { getDocument, GlobalWorkerOptions, version } from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
import "pdfjs-dist/web/pdf_viewer.css";

// Set worker source to CDN
GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;

type PDFViewerProps = {
  url: string;
};

const PDFViewer: React.FC<PDFViewerProps> = ({ url }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);

  useEffect(() => {
    let isMounted = true;
    setError(null);
    setNumPages(null);

    const loadingTask = getDocument({
      url,
      withCredentials: false,
    });

    loadingTask.promise
      .then((pdf: PDFDocumentProxy) => {
        if (!isMounted) return;
        setNumPages(pdf.numPages);
        
        const renderPages = async () => {
          for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const viewport = page.getViewport({ scale: 1.5 });

            const canvas = canvasRefs.current[pageNum - 1];
            if (canvas) {
              const context = canvas.getContext("2d");
              if (context) {
                canvas.height = viewport.height;
                canvas.width = viewport.width;
                await page.render({
                  canvasContext: context,
                  viewport,
                }).promise;
              }
            }
          }
        };

        return renderPages();
      })
      .catch((err) => {
        if (isMounted) {
          setError("Failed to load PDF. Please check the file URL.");
          console.error("PDF loading error:", err);
        }
      });

    return () => {
      isMounted = false;
      loadingTask.destroy();
    };
  }, [url]);

  if (error) {
    return (
      <div style={{ 
        color: "#e74c3c", 
        padding: "20px", 
        textAlign: "center",
        border: "1px solid #e74c3c",
        borderRadius: "4px",
        backgroundColor: "#fdecea"
      }}>
        {error}
        <div style={{ marginTop: "10px" }}>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{
            color: "#3498db",
            textDecoration: "underline"
          }}>
            Try opening the PDF directly
          </a>
        </div>
      </div>
    );
  }

  if (numPages === null) {
    return (
      <div style={{ 
        padding: "20px", 
        textAlign: "center",
        color: "#666"
      }}>
        Loading PDF document...
      </div>
    );
  }

  return (
    <div style={{ marginTop: "20px" }}>
      {Array.from({ length: numPages }, (_, index) => (
        <div key={`page-${index}`} style={{ 
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
        }}>
          <canvas
            ref={(ref) => (canvasRefs.current[index] = ref)}
            style={{ 
              display: "block", 
              maxWidth: "100%", 
              border: "1px solid #ddd",
              margin: "0 auto"
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default PDFViewer;